import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { useNavigate } from 'react-router-dom'
import { autoDetectUserTimezone, shouldAutoDetectTimezone } from './timezone-auto-detect'

interface AuthContextType {
    session: Session | null
    user: User | null
    signOut: () => Promise<void>
    signInWithGoogle: () => Promise<void>
    signInWithFacebook: () => Promise<void>
    signInWithEmail: (email: string) => Promise<void>
    updateProfile: (profile: { [key: string]: any }) => Promise<void>
    isSuperAdmin: boolean
    loading: boolean
}

const AuthContext = createContext<AuthContextType>({
    session: null,
    user: null,
    signOut: async () => { },
    signInWithGoogle: async () => { },
    signInWithFacebook: async () => { },
    signInWithEmail: async () => { },
    updateProfile: async () => { },
    isSuperAdmin: false,
    loading: true
})

// Single source of truth for auth state
let globalAuthInitialized = false

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [session, setSession] = useState<Session | null>(null)
    const [user, setUser] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)
    const [initialized, setInitialized] = useState(false)
    const navigate = useNavigate()
    
    // Use a ref to track current session to avoid stale closures
    const sessionRef = useRef<Session | null>(null)

    // Memoize the sync customer function
    const syncCustomer = useCallback(async () => {
        const workerUrl = import.meta.env.VITE_WORKERS_URL
        try {
            const currentSession = await supabase.auth.getSession()
            if (!currentSession.data.session?.access_token) return
            const token = currentSession.data.session.access_token

            const response = await fetch(`${workerUrl}/billing-functions/customer_sync`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
            
            if (response.status === 401) {
                console.error('Unauthorized access, clearing session')
                // Reset session state but don't trigger signOut to prevent loops
                setSession(null)
                setUser(null)
                // Clear localStorage auth data
                clearAuthData()
                return
            }

            const data = await response.json()
            console.log('Customer synced', data)
        } catch (error) {
            console.error('Error syncing customer:', error)
        }
    }, [])
    
    // Helper to clear all auth data from localStorage
    const clearAuthData = () => {
        // Clear all Supabase auth data
        for (const key in localStorage) {
            if (key.includes('supabase.auth') || key.includes('sb-')) {
                localStorage.removeItem(key)
            }
        }
        // Also clear cookies related to auth
        document.cookie.split(';').forEach(cookie => {
            const [name] = cookie.trim().split('=')
            if (name.includes('sb-')) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
            }
        })
    }

    // Memoize the brands check function
    const checkAndHandleBrands = useCallback(async () => {
        if (window.location.pathname !== '/' && window.location.pathname !== '/home') return

        const { data: brands, error: brandsError } = await supabase
        .from('brands')
        .select('id')
        .neq('status', 'DELETED')  // Only exclude deleted brands
        .limit(1)

        if (brandsError) {
            console.error('Error fetching brands:', brandsError)
            return
        }

        if (!brands?.length) {
            console.log('No brands found, redirecting to onboarding')
            navigate('/onboarding')
        }
    }, [navigate])

    // Memoize the session update function
    const handleSessionUpdate = useCallback(async (newSession: Session | null, event?: string) => {
        console.log('[Auth] handleSessionUpdate called', {
            event,
            newSessionId: newSession?.user?.id,
            currentSessionId: session?.user?.id,
            sessionRefId: sessionRef.current?.user?.id
        });
        
        const sessionChanged = JSON.stringify(newSession) !== JSON.stringify(session)
        if (!sessionChanged) {
            console.log('[Auth] Session unchanged, skipping update')
            return
        }

        // If there's already a session and this is a SIGNED_IN event, don't do anything
        if( session && event === 'SIGNED_IN') {
            console.log('Session already exists, skipping session update')
            return;
        }

        console.log('[Auth] Updating session state')
        setSession(newSession)
        setUser(newSession?.user ?? null)
        sessionRef.current = newSession

        if (newSession?.user && (!event || ['SIGNED_IN', 'USER_UPDATED', 'INITIAL_SESSION'].includes(event))) {
            // Auto-detect timezone for users without one set (non-blocking)
            const handleTimezoneDetection = async () => {
                try {
                    const needsTimezone = await shouldAutoDetectTimezone(newSession.user)
                    if (needsTimezone) {
                        console.log('[Auth] Auto-detecting timezone for user')
                        const timezoneResult = await autoDetectUserTimezone(newSession.user, {
                            updateProfile: true,
                            silentMode: true // Keep silent to avoid interfering with auth flow
                        })
                        
                        if (timezoneResult.detected && timezoneResult.timezone) {
                            console.log('[Auth] Successfully auto-detected timezone:', timezoneResult.timezone)
                        }
                    }
                } catch (error) {
                    console.error('[Auth] Error during timezone auto-detection:', error)
                }
            }
            
            await Promise.all([
                checkAndHandleBrands(),
                syncCustomer(),
                handleTimezoneDetection()
            ])
        }
    }, [session, checkAndHandleBrands, syncCustomer])

    // Memoize the context value
    const contextValue = useMemo(() => ({
        session,
        user,
        signOut: async () => {
            try {
                // Check if session exists before signing out
                const { data } = await supabase.auth.getSession()
                
                if (data.session) {
                    const { error } = await supabase.auth.signOut({ scope: 'global' })
                    if (error) throw error
                }
                
                // Reset session state
                setSession(null)
                setUser(null)
                
                // Clear all auth data
                clearAuthData()
                
                // Force a full page reload to clear React state
                window.location.href = '/?forceLogout=' + Date.now()
            } catch (error) {
                console.error('Error signing out:', error)
                // Clear auth data even if there's an error
                clearAuthData()
                // Force a full reload
                window.location.href = '/?forceLogout=' + Date.now()
            }
        },
        signInWithGoogle: async () => {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/auth/callback`
                }
            })
            if (error) {
                console.error('Error signing in with Google:', error)
                throw error
            }
        },
        signInWithFacebook: async () => {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'facebook',
                options: {
                    redirectTo: `${window.location.origin}/auth/callback`
                }
            })
            if (error) {
                console.error('Error signing in with Facebook:', error)
                throw error
            }
        },
        signInWithEmail: async (email: string) => {
            const { error } = await supabase.auth.signInWithOtp({
                email,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/callback`
                }
            })
            if (error) {
                console.error('Error signing in with Email:', error)
                throw error
            }
        },
        updateProfile: async (profile: { [key: string]: any }) => {
            try {
                // First update auth user metadata
                const { error: authError } = await supabase.auth.updateUser({
                    data: profile
                });
                if (authError) throw authError;

                // Get the current session to get the user ID
                const { data: { session } } = await supabase.auth.getSession();
                if (!session?.user?.id) throw new Error('No user session found');

                // Prepare notification preferences
                const notificationPreferences = {
                    emailNotifications: profile.emailNotifications
                };

                // Then update marketing profile
                const { error: profileError } = await supabase
                    .schema('marketing_and_customer_success')
                    .from('profiles')
                    .upsert({
                        id: session.user.id,
                        full_name: profile.full_name,
                        email_opt_in: profile.emailNotifications,
                        preferred_language: profile.language,
                        timezone: profile.timezone,
                        notificationPreferences: notificationPreferences,
                        updated_at: new Date().toISOString()
                    });
                if (profileError) throw profileError;
            } catch (error) {
                console.error('Error updating profile:', error);
                throw error;
            }
        },
        isSuperAdmin: user?.app_metadata?.is_super_admin ?? false,
        loading: loading || !initialized
    }), [session, user, loading, initialized])

    useEffect(() => {
        if (globalAuthInitialized) return
        globalAuthInitialized = true

        let mounted = true
        
        // Check if we're coming from a logout
        const urlParams = new URLSearchParams(window.location.search)
        const forceLogout = urlParams.get('forceLogout')
        
        // If forceLogout param exists, clear any persisting auth data
        if (forceLogout) {
            clearAuthData()
            // Remove the parameter from URL to prevent issues on refresh
            const newUrl = window.location.pathname
            window.history.replaceState({}, document.title, newUrl)
        }
        
        async function initializeAuth() {
            try {
                // Get initial session
                const { data: { session: initialSession }, error } = await supabase.auth.getSession()
                if (error) throw error
                if (!mounted) return

                if (initialSession) {
                    sessionRef.current = initialSession
                    await handleSessionUpdate(initialSession, 'INITIAL_SESSION')
                }

                console.log('[Auth] Setting loading to false and initialized to true')
                setLoading(false)
                setInitialized(true)

                // Listen for auth changes
                const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
                    if (!mounted) return

                    // Ignore SIGNED_IN events if we already have a session with the same user
                    // This prevents re-initialization when tabs regain focus
                    if (event === 'SIGNED_IN' && sessionRef.current?.user?.id && sessionRef.current.user.id === newSession?.user?.id) {
                        console.log('Ignoring SIGNED_IN event - user already authenticated')
                        return
                    }

                    console.log('Auth state changed:', {
                        event,
                        userId: newSession?.user?.id
                    })

                    // await handleSessionUpdate(newSession, event)
                    handleSessionUpdate(newSession, event)
                })

                return () => {
                    mounted = false
                    subscription.unsubscribe()
                }
            } catch (error) {
                console.error('Auth initialization error:', error)
                if (mounted) {
                    setLoading(false)
                    setInitialized(true)
                }
            }
        }

        initializeAuth()
    }, [handleSessionUpdate])

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

// Simple auth error handler - returns false for now to preserve existing error handling
export const handleAuthError = (_error: any): boolean => {
    // TODO: Implement proper auth error handling if needed
    return false
} 